import { useEffect, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  AutoComplete,
  AutoCompleteCompleteEvent,
} from "primereact/autocomplete";
import classNames from "classnames";
import Icon from "../Icon";
import useTranslations from "../../../core/i18n/useTranslations";
import PrimeTooltip from "../PrimeTooltip/PrimeTooltip";
import { SingleChoice } from "./SingleChoice";
import { MultiChoice } from "./MultiChoice";
import {
  IAutocompleteField,
  IValidatioAutocomplite,
} from "../../../core/api/autocomplite/types";
import {
  getErrorMessage,
  filterAlternatives,
  prettifyFreeTextInput,
} from "./ControllerAutocomplite";

import styles from "./Autocomplete.module.css";
import inputStyles from "../Inputs/Input.module.css";

interface IAutocompleteProps {
  name: string;
  defaultLabel: string;
  defaultPlaceholder?: string;
  defaultValidation?: Record<string, any>;
  fieldData?: IAutocompleteField;
  dataCy?: string;
}

const Autocomplete = ({
  name,
  defaultLabel,
  defaultPlaceholder,
  defaultValidation,
  fieldData,
  dataCy,
}: IAutocompleteProps) => {
  const t = useTranslations();
  const multiChoice = fieldData?.inputType === "MultiChoice";
  const singleChoice = fieldData?.inputType === "SingleChoice";
  const multiChoiceFreeText = fieldData?.inputType === "MultiChoiceFreeText";
  const multiInputRef = useRef<HTMLInputElement>(null);
  const labelRef = useRef<HTMLLabelElement>(null);
  const labelWrapperRef = useRef<HTMLDivElement>(null);

  const [showtooltip, setShowtooltip] = useState(false);
  const [items, setItems] = useState<string[]>([]);
  const [rules, setRules] = useState<{ [key: string]: IValidatioAutocomplite }>(
    {},
  );

  const {
    formState: { errors },
    control,
    setValue,
  } = useFormContext();

  const clearMultiInput = () => {
    if (multiInputRef.current) {
      multiInputRef.current.value = "";
    }
  };

  const filterItems = (event: AutoCompleteCompleteEvent) => {
    const query = event.query;
    setItems(filterAlternatives(query, fieldData?.alternatives));
  };

  // MultiChoiceFreeText need handle spaces and comas so value passed to back-end was prettified as a plain string
  const multiChoiceFreeTextHandler = (e: React.KeyboardEvent, val: string) => {
    if (e.key === "Enter") {
      // current entered value
      const target = e.target as HTMLInputElement;
      // remove extra spaces and commas
      const valueToSet = prettifyFreeTextInput(target.value);

      if (!valueToSet) return;

      // check if value is already in the list and remove it if true
      const parsedValue: string[] = val ? val.toLowerCase().split(", ") : [];
      if (parsedValue.includes(target.value.toLowerCase())) {
        clearMultiInput();
        return;
      }

      // set new value and clear input
      setValue(name, val ? [val, valueToSet].join(", ") : valueToSet);
      clearMultiInput();
    }
  };

  useEffect(() => {
    if (labelRef.current && labelWrapperRef.current) {
      setShowtooltip(
        labelWrapperRef.current.offsetWidth < labelRef.current.offsetWidth,
      );
    }
  }, [labelRef.current]);

  useEffect(() => {
    if (fieldData?.validations) {
      const value = {
        ...fieldData.validations,
        pattern: fieldData.validations?.regex
          ? new RegExp(fieldData.validations.regex)
          : null,
      };
      setRules({ [name]: value });
    }
    if (fieldData?.alternatives) {
      setItems(fieldData.alternatives);
    }
  }, [fieldData]);

  return (
    <Controller
      name={name}
      control={control}
      rules={
        Object.keys(rules).length
          ? {
              ...rules[name],
              validate: (v) => (v?.length ? !!v.trim() : true),
            }
          : defaultValidation
      }
      render={({ field }) => (
        <div className={styles.autocompleteWrapper}>
          <div ref={labelWrapperRef} className={styles.autocompleteLabel}>
            {showtooltip && <PrimeTooltip target={`#${name}`} />}
            {rules[name]?.required && (
              <span className={inputStyles.requiredIcon}>*</span>
            )}
            <label
              ref={labelRef}
              id={name}
              htmlFor={name}
              data-pr-tooltip={fieldData?.label || defaultLabel}
            >
              {fieldData?.label || defaultLabel}
            </label>
          </div>
          {singleChoice && (
            <SingleChoice
              withClearOption
              field={field}
              name={name}
              options={items}
              placeholder={fieldData?.inlineHelp || defaultPlaceholder}
              hasError={!!errors[name]}
            />
          )}
          {multiChoice && (
            <MultiChoice
              name={name}
              options={items}
              field={field}
              placeholder={fieldData?.inlineHelp || defaultPlaceholder}
              hasError={!!errors[name]}
            />
          )}
          {!singleChoice && !multiChoice && (
            <AutoComplete
              aria-label={fieldData?.label || defaultLabel}
              delay={0}
              data-cy={dataCy}
              ref={field.ref}
              inputRef={multiInputRef}
              showEmptyMessage={!!fieldData?.alternatives.length}
              emptyMessage={t("Common:no_options")}
              multiple={multiChoiceFreeText}
              placeholder={
                multiChoiceFreeText && field.value?.length
                  ? ""
                  : (fieldData?.inlineHelp ?? defaultPlaceholder)
              }
              className={classNames({
                [styles.freeText]: true,
                [styles.multiSelect]: multiChoiceFreeText,
                [styles.filledBorder]: !!field.value,
                [styles.validationError]: !!errors[name],
              })}
              inputId={name}
              value={
                multiChoiceFreeText && field.value?.length
                  ? field.value?.replaceAll(/\s*,?\s*\n/g, ", ").split(", ")
                  : field.value
              }
              suggestions={items}
              completeMethod={filterItems}
              onChange={(e) => {
                if (multiChoiceFreeText) {
                  field.onChange(e.value?.length ? e.value?.join(", ") : null);
                  return;
                }
                field.onChange(e);
              }}
              onBlur={clearMultiInput}
              onKeyDown={(e) => {
                if (multiChoiceFreeText)
                  multiChoiceFreeTextHandler(e, field.value);
              }}
              panelStyle={{ maxWidth: multiInputRef.current?.offsetWidth }}
            />
          )}
          {errors?.[name] && (
            <div className={inputStyles.validationError}>
              <Icon name="exclamation-circle" size={14} />
              {getErrorMessage(
                t,
                errors[name].type as string,
                rules[name]?.maxLength?.toString() ??
                  defaultValidation?.maxLength?.toString(),
              )}
            </div>
          )}
        </div>
      )}
    />
  );
};

export default Autocomplete;
